import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

const IGNORED_ERRORS = [
  // Supports exact string match: { message: 'Some error' }
  // Supports regex match: { message: /pattern/i }
  { message: 'Cancel rendering route' },
  // Transient network errors on iOS Safari / Chrome — not actionable
  { message: /load failed/i },
  { message: /failed to fetch/i },
  { message: /^timeout on /i }
];

function shouldIgnoreError(event) {
  // Check cancelled flag (Next.js navigation)
  if (event.originalError?.cancelled) {
    return true;
  }

  // Collect all error messages in the chain (main error + caused-by entries)
  const errorMessages = [
    ...(event.errors || []).map(e => e.errorMessage || ''),
    event.originalError?.message || ''
  ].filter(Boolean);

  return IGNORED_ERRORS.some(({ message }) =>
    errorMessages.some(msg => {
      if (message instanceof RegExp) return message.test(msg);
      return msg === message;
    })
  );
}

const isBrowser = typeof window !== 'undefined';
const isServer = !isBrowser;

const STAGING_DOMAINS = [
  '.azstaging.net',
  '.azstage.in',
  '.digit-az.app',
  '.vercel.app'
];

const matchesStagingDomain = url =>
  STAGING_DOMAINS.some(domain => url?.includes(domain));

const getReleaseStage = () => {
  if (isBrowser) {
    const { hostname } = window.location;

    if (hostname.match(/^(?:.+\.)?bina\.az$/)) {
      return 'production';
    }

    if (matchesStagingDomain(hostname)) {
      return process.env.NEXT_PUBLIC_VERCEL_GITHUB_BRANCH || 'staging';
    }

    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    ) {
      return 'local';
    }

    return 'local';
  }

  if (process.env.VERCEL_ENV === 'production') {
    return 'production';
  }

  if (matchesStagingDomain(process.env.VERCEL_URL)) {
    return (
      process.env.VERCEL_GITHUB_BRANCH ||
      process.env.VERCEL_GIT_COMMIT_REF ||
      'staging'
    );
  }

  if (process.env.VERCEL_GITHUB_BRANCH) {
    return process.env.VERCEL_GITHUB_BRANCH;
  }

  return 'local';
};

const releaseStage = getReleaseStage();

const apiKey = process.env.BUGSNAG_API_KEY || 'DO_NOT_SEND';

const commonErrorSettings = {
  autoDetectErrors: true,
  enabledErrorTypes: {
    unhandledExceptions: true,
    unhandledRejections: true
  }
};

const config = {
  apiKey,
  appVersion: process.env.APP_VERSION,
  releaseStage,
  enabledReleaseStages: null,
  logger: null,
  ...commonErrorSettings,
  ...(isServer && {
    maxBreadcrumbs: 10,
    maxEvents: 1
  }),
  ...(isBrowser && {
    consoleBreadcrumbsEnabled: true,
    collectUserIp: false
  }),
  onError(event) {
    if (shouldIgnoreError(event)) {
      return false;
    }

    if (apiKey === 'DO_NOT_SEND') {
      if (
        !process.env.NEXT_PUBLIC_E2E &&
        (releaseStage === 'local' || process.env.NODE_ENV === 'development')
      ) {
        const context = isBrowser ? 'Browser' : 'Server';
        console.log(
          `[Bugsnag ${context}] Error not sent (API key not configured):`,
          {
            releaseStage: event.app.releaseStage,
            error: event.errors?.[0]?.errorMessage,
            context: event.context
          }
        );
      }
      return false;
    }

    if (
      !process.env.NEXT_PUBLIC_E2E &&
      (releaseStage === 'local' || process.env.NODE_ENV === 'development')
    ) {
      const context = isBrowser ? 'Browser' : 'Server';
      console.log(`[Bugsnag ${context}] Sending error to Bugsnag:`, {
        releaseStage: event.app.releaseStage,
        error: event.errors?.[0]?.errorMessage,
        context: event.context
      });
    }

    return true;
  },
  plugins: isBrowser ? [new BugsnagPluginReact()] : []
};

if (
  !process.env.NEXT_PUBLIC_E2E &&
  (releaseStage === 'local' || process.env.NODE_ENV === 'development')
) {
  const context = isBrowser ? 'Browser' : 'Server';
  console.log(
    `[Bugsnag ${context}] Initialized (releaseStage: ${releaseStage})`
  );
}

Bugsnag.start(config);

export default Bugsnag;
